import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { normalizeCommercialHref, type CtaZone } from '@/lib/commercialLinks';

const SEL = 'a[href^="/"], a[href*="bpo-contact.com/"]';

function zoneOf(el: Element): CtaZone {
  if (el.closest('header')) return 'header';
  if (el.closest('footer')) return 'footer';
  if (el.closest('.fixed, .sticky')) return 'sticky';
  return 'main';
}

function fix(a: HTMLAnchorElement, pathname: string): string | null {
  const orig = a.dataset.ctaOrig || a.getAttribute('href') || '';
  const label = a.innerText || a.getAttribute('aria-label') || '';
  const next = normalizeCommercialHref(orig, label, pathname, zoneOf(a));
  if (next && a.getAttribute('href') !== next) {
    a.dataset.ctaOrig = orig;
    a.setAttribute('href', next);
  }
  return next;
}

/** Rewrites commercial links of the current public page to the /start widget (href + click). */
export default function CommercialLinkNormalizer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let raf = 0;
    const scan = () => {
      raf = 0;
      document.querySelectorAll<HTMLAnchorElement>(SEL).forEach((a) => fix(a, pathname));
    };
    scan();
    const mo = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(scan); });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.(SEL) as HTMLAnchorElement | null;
      if (!a || a.target === '_blank') return;
      const next = fix(a, pathname);
      if (!next) return;
      e.preventDefault();
      e.stopPropagation();
      navigate(next);
    };
    document.addEventListener('click', onClick, true);
    return () => { mo.disconnect(); document.removeEventListener('click', onClick, true); if (raf) cancelAnimationFrame(raf); };
  }, [pathname, navigate]);

  return null;
}
